import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Button, message } from "antd";
import { FaArrowLeft, FaCodeCompare } from "react-icons/fa6";
import { useSessionResponses } from "../../api/SessionResponsesAPI";
import {
  useCompetenciesByIds,
  useAllCompetencies,
} from "../../api/CompetenciesAPI";
import ComparisonCinemaMap from "../../components/compare/ComparisonCinemaMap";
import ComparisonReportWeb from "../../components/compare/ComparisonReportWeb";
import ComparisonCinemaMapPDF from "../../components/compare/ComparisonCinemaMapPDF";
import ComparisonDistributionPDF from "../../components/compare/ComparisonDistributionPDF";
import ComparisonCompetencyTablePDF from "../../components/compare/ComparisonCompetencyTablePDF";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

// PDF styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
    fontFamily: "Poppins",
  },
  header: {
    marginBottom: 15,
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#333333",
  },
  sessionDetailsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  leftDetails: {
    fontSize: 8,
    color: "#666666",
  },
  rightDetails: {
    fontSize: 8,
    textAlign: "right",
    color: "#666666",
  },
  detailLine: {
    marginBottom: 3,
  },
  divider: {
    borderBottom: "1 solid #e0e0e0",
    marginTop: 4,
    marginBottom: 4,
  },
  sectionHeader: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 15,
    color: "#333333",
  },
});

const PHASES = [
  "Empathize",
  "Define",
  "Ideate",
  "Prototype",
  "Test",
  "Communicate",
];

// Helper function to generate comparison chart images from data
const generateComparisonChartImages = async (
  session1Competencies,
  session2Competencies
) => {
  console.log("generateComparisonChartImages called");

  if (!session1Competencies || !session2Competencies) {
    console.log("No competency data provided to generateComparisonChartImages");
    return {};
  }

  try {
    // Dynamically import Chart.js
    const ChartModule = await import("chart.js/auto");
    const Chart = ChartModule.Chart || ChartModule.default;
    console.log("Chart.js imported successfully");

    const chartImages = {};

    // Process data to get phase-based breakdown for both sessions
    const session1PhaseData = {};
    const session2PhaseData = {};

    PHASES.forEach((phase) => {
      session1PhaseData[phase] = {
        skills: 0,
        knowledge: 0,
        mindset: 0,
        instruments: 0,
        total: 0,
      };
      session2PhaseData[phase] = {
        skills: 0,
        knowledge: 0,
        mindset: 0,
        instruments: 0,
        total: 0,
      };
    });

    // Process session 1 competencies
    session1Competencies.forEach((comp) => {
      const phase = comp.abilities?.phase || "Unknown";
      const category = comp.category === "tool" ? "instruments" : comp.category;
      if (
        PHASES.includes(phase) &&
        session1PhaseData[phase][category] !== undefined
      ) {
        session1PhaseData[phase][category]++;
        session1PhaseData[phase].total++;
      }
    });

    // Process session 2 competencies
    session2Competencies.forEach((comp) => {
      const phase = comp.abilities?.phase || "Unknown";
      const category = comp.category === "tool" ? "instruments" : comp.category;
      if (
        PHASES.includes(phase) &&
        session2PhaseData[phase][category] !== undefined
      ) {
        session2PhaseData[phase][category]++;
        session2PhaseData[phase].total++;
      }
    });

    console.log("Session 1 phase data:", session1PhaseData);
    console.log("Session 2 phase data:", session2PhaseData);

     for (const phase of PHASES) {
      const session1Data = session1PhaseData[phase];
      const session2Data = session2PhaseData[phase];
      const categories = ["skills", "knowledge", "mindset", "instruments"];

      // Create a temporary canvas
      const canvas = document.createElement("canvas");
      canvas.width = 400;
      canvas.height = 300;

      const ctx = canvas.getContext("2d");

      const categoryColors = {
        skills: "#4CAF50",
        knowledge: "#2196F3",
        mindset: "#FF9800",
        instruments: "#9C27B0",
      };

      const lighterCategoryColors = {
        skills: "#C8E6C9", // Very light green
        knowledge: "#BBDEFB", // Very light blue
        mindset: "#FFE0B2", // Very light orange
        instruments: "#E1BEE7", // Very light purple
      };

      const chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: categories.map(
            (cat) => cat.charAt(0).toUpperCase() + cat.slice(1)
          ),
          datasets: [
            {
              label: "Session 1",
              data: categories.map((cat) => session1Data[cat]),
              backgroundColor: categories.map((cat) => categoryColors[cat]),
              borderColor: categories.map((cat) => "#000000"),
              borderWidth: 1,
            },
            {
              label: "Session 2",
              data: categories.map((cat) => session2Data[cat]),
              backgroundColor: categories.map(
                (cat) => lighterCategoryColors[cat]
              ),
              borderColor: categories.map((cat) => "#000000"),
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: false,
          animation: false, // Disable animation for faster rendering
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: {
                font: {
                  family: "Poppins, sans-serif",
                  size: 12,
                },
              },
            },
            tooltip: {
              enabled: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                font: {
                  family: "Poppins, sans-serif",
                  size: 14,
                  weight: "bold",
                },
              },
              grid: {
                color: "#e0e0e0",
              },
            },
            x: {
              ticks: {
                font: {
                  family: "Poppins, sans-serif",
                  size: 14,
                  weight: "bold",
                },
              },
              grid: {
                display: false,
              },
            },
          },
        },
      });

      // Wait for chart to render
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Convert to image
      const imageData = canvas.toDataURL("image/png");
      chartImages[phase] = imageData;
      console.log(
        `Comparison chart for ${phase} created, image data length:`,
        imageData.length
      );

      // Cleanup
      chart.destroy();
    }

    console.log(
      "All comparison charts generated, total:",
      Object.keys(chartImages).length
    );
    return chartImages;
  } catch (error) {
    console.error("Error in generateComparisonChartImages:", error);
    return {};
  }
};

const ComparisonView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isGenerating, setIsGenerating] = useState(false);

  // Get session IDs from query parameters
  const session1 = searchParams.get("session1");
  const session2 = searchParams.get("session2");
  const sessionIds = [session1, session2].filter(
    (id) => id !== null && id !== ""
  );

  // Fetch session responses for both sessions
  const { data: session1Responses, isLoading: session1Loading } =
    useSessionResponses(session1);
  const { data: session2Responses, isLoading: session2Loading } =
    useSessionResponses(session2);

  // Get competency IDs from session responses
  const session1CompetencyIds =
    session1Responses?.map((response) => response.competency_id) || [];
  const session2CompetencyIds =
    session2Responses?.map((response) => response.competency_id) || [];

  // Fetch competency details using the IDs
  const { data: session1Competencies, isLoading: session1CompetenciesLoading } =
    useCompetenciesByIds(session1CompetencyIds);

  const { data: session2Competencies, isLoading: session2CompetenciesLoading } =
    useCompetenciesByIds(session2CompetencyIds);

  // Fetch all competencies for the comparison
  const { data: allCompetenciesData, isLoading: allCompetenciesLoading } =
    useAllCompetencies();


  const handleDownloadPDF = async () => {
    // Wait for all data to be loaded
    if (
      session1Loading ||
      session2Loading ||
      session1CompetenciesLoading ||
      session2CompetenciesLoading ||
      allCompetenciesLoading
    ) {
      message.warning("Please wait for data to load before downloading PDF");
      return;
    }

    try {
      setIsGenerating(true);
      console.log("=== Starting PDF Generation ===");

      // Generate chart images first
      console.log("Step 1: Generating comparison chart images...");
      const chartImages = await generateComparisonChartImages(
        session1Competencies,
        session2Competencies
      );
      console.log("Chart images generated:", Object.keys(chartImages));
      console.log("Number of charts:", Object.keys(chartImages).length);

      // Check if we have valid image data
      if (Object.keys(chartImages).length === 0) {
        console.error("No chart images were generated!");
        message.error("Failed to generate charts. Check console for details.");
        setIsGenerating(false);
        return;
      }

      const doc = (
        <Document>
          <Page size="A4" style={styles.page}>
            <View style={styles.header}>
              <Text style={styles.title}>Competency Comparison Report</Text>

              <View style={styles.sessionDetailsContainer}>
                <View style={styles.leftDetails}>
                  <Text style={styles.detailLine}>
                    Session 1 ID: {session1}
                  </Text>
                  <Text style={styles.detailLine}>
                    Session 2 ID: {session2}
                  </Text>
                  <Text style={styles.detailLine}>Total Sessions: 2</Text>
                </View>

                <View style={styles.rightDetails}>
                  <Text style={styles.detailLine}>
                    Session 1 Competencies: {session1Competencies?.length || 0}
                  </Text>
                  <Text style={styles.detailLine}>
                    Session 2 Competencies: {session2Competencies?.length || 0}
                  </Text>
                  <Text style={styles.detailLine}>
                    Total Responses:{" "}
                    {(session1Responses?.length || 0) +
                      (session2Responses?.length || 0)}
                  </Text>
                </View>
              </View>

              <View style={styles.divider} />
            </View>

            <View>
              <Text style={styles.sectionHeader}>The Big Picture</Text>
              <ComparisonCinemaMapPDF
                allCompetenciesData={allCompetenciesData || []}
                session1Responses={session1Responses || []}
                session2Responses={session2Responses || []}
              />
            </View>
          </Page>

          <Page size="A4" style={styles.page}>
            <View style={styles.header}>
              <Text style={styles.title}>The Distribution</Text>
              <View style={styles.divider} />
            </View>

            <ComparisonDistributionPDF chartImages={chartImages} />
          </Page>

          <Page size="A4" style={styles.page}>
            <View style={styles.header}>
              <Text style={styles.title}>The Details</Text>
              <View style={styles.divider} />
            </View>

            {PHASES.map((phase) => (
              <ComparisonCompetencyTablePDF
                key={phase}
                session1Competencies={session1Competencies || []}
                session2Competencies={session2Competencies || []}
                phase={phase}
              />
            ))}
          </Page>
        </Document>
      );

      console.log("Step 2: Generating PDF blob...");
      const blob = await pdf(doc).toBlob();
      console.log("PDF blob created, size:", blob.size);

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Comparison-Report-${session1}-${session2}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log("=== PDF Generated Successfully! ===");
      message.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("=== Error generating PDF ===");
      console.error("Error:", error);
      console.error("Stack:", error.stack);
      message.error(`Error generating PDF: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Show loading state while fetching data
  if (
    session1Loading ||
    session2Loading ||
    session1CompetenciesLoading ||
    session2CompetenciesLoading ||
    allCompetenciesLoading
  ) {
    return (
      <div className="py-6 px-10">
        <Card className="mb-4 bg-white rounded-lg">
          <div className="text-center py-8 text-gray-500">
            Loading session data...
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="py-6 px-10">
      <Card className="mb-4 bg-white text-primary rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FaCodeCompare className="text-2xl text-primary-500 mr-4" />
            <div>
              <div className="flex items-center gap-4 mb-1">
                <h1 className="font-semibold text-lg m-0">
                  Session Comparison
                </h1>
                <button
                  className={`text-white text-xs px-3 py-1.5 rounded transition-colors duration-200 ${
                    session1Loading ||
                    session2Loading ||
                    session1CompetenciesLoading ||
                    session2CompetenciesLoading ||
                    allCompetenciesLoading ||
                    isGenerating
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:bg-primaryLight"
                  }`}
                  onClick={handleDownloadPDF}
                  disabled={
                    session1Loading ||
                    session2Loading ||
                    session1CompetenciesLoading ||
                    session2CompetenciesLoading ||
                    allCompetenciesLoading ||
                    isGenerating
                  }
                >
                  {session1Loading ||
                  session2Loading ||
                  session1CompetenciesLoading ||
                  session2CompetenciesLoading ||
                  allCompetenciesLoading
                    ? "Loading..."
                    : isGenerating
                    ? "Generating..."
                    : "Download PDF"}
                </button>
                <button
                  className="border-primary border-2 hover:bg-primary hover:text-white text-primary text-xs px-3 py-1.5 rounded transition-colors duration-200"
                  onClick={() => navigate(`/compare`)}
                >
                  Back to Comparison List
                </button>
              </div>
              <p className="text-gray-500 m-0">
                Comparing {sessionIds.length} sessions:{" "}
                <span className="font-mono">{sessionIds.join(", ")}</span>
              </p>
            </div>
          </div>

         
        </div>
      </Card>

      {/* Comparison Report */}
      <ComparisonReportWeb />
    </div>
  );
};

export default ComparisonView;
